import { message } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";

import { useWebviewContext } from "@btnet/utilities-webview";

import { BdsIconFlag, BdsIconPlans } from "@buildertrend/components";

import { showApixErrorMessage } from "utilities/api/apix-handler";
import {
    getPreviousPathname,
    getPreviousTitle,
} from "utilities/flatRoutes/usePreviousLocation/usePreviousLocation";
import { type IPreviousLocationState } from "utilities/flatRoutes/usePreviousLocation/usePreviousLocation.types";
import { lazyLoadComponent } from "utilities/LazyLoad.utility";
import { routes } from "utilities/routes";

import { type ExportHeaderResponse } from "commonComponents/btWrappers/BTSocialMediaButtons/FileShare.types";
import { BTTabs } from "commonComponents/btWrappers/BTTabs/BTTabs";

import {
    EntityType,
    type PlanPage,
    plansSharePostV2,
    type PlansShareResponse,
    usePlansBySetIdGetV3,
    usePlansExternalDetailsBySetIdGetV3,
} from "handlers";

import { PlansCard } from "entity/Plans/PlansCard/PlansCard";
import { PlansCardSkeleton } from "entity/Plans/PlansCard/PlansCardSkeleton";
import { type SharePlanType } from "entity/Plans/PlansList/PlansList";

import "./LinkedPlanSetViewer.less";

const GenerateQRCodeModal = lazyLoadComponent(
    () => import("commonComponents/entity/sharing/GenerateQRCodeModal"),
);

interface ILinkedPlanSetViewerProps {
    jobId: number;
    setId: string;
    sourceEntityType?: EntityType;
    sourceEntityId?: number;
    isExternal?: boolean;
    shareToken?: string;
    previousPageTitle?: string;
    canLinkPlanPages: boolean;
    linkedPlanPageIds?: Set<string>;
    onTogglePlanPageLink?: (plan: PlanPage) => void;
}

const ANONYMOUS_TOKEN = "anonymous";
const noOp = async () => {};

type PlansFilterTab = "flagged" | "fullSet";

export function LinkedPlanSetViewer(props: ILinkedPlanSetViewerProps) {
    const {
        jobId,
        setId,
        sourceEntityId,
        sourceEntityType,
        isExternal,
        shareToken,
        previousPageTitle,
        canLinkPlanPages,
        linkedPlanPageIds,
        onTogglePlanPageLink,
    } = props;

    const authenticated = usePlansBySetIdGetV3(
        setId,
        {
            jobId,
            bidId:
                sourceEntityType === EntityType.BidPackage && sourceEntityId
                    ? sourceEntityId
                    : undefined,
        },
        { query: { enabled: !isExternal && !!jobId, retry: false } },
    );

    const external = usePlansExternalDetailsBySetIdGetV3(setId, {
        query: { enabled: !!isExternal },
    });

    const { data: planPageData, isLoading, error } = isExternal ? external : authenticated;

    const [showGenerateQRCodeModal, setShowGenerateQRCodeModal] = useState<boolean>(false);
    const [shareData, setShareData] = useState<PlansShareResponse | null>(null);
    const [planPageTitle, setPlanPageTitle] = useState("");
    const location = useLocation();
    const navigate = useNavigate();
    const webviewContext = useWebviewContext();

    const isInWebview = !!webviewContext;

    if (error) {
        showApixErrorMessage(error);
    }

    const onCopySharePageLink = useCallback(
        async (pageId?: string, _shareType?: SharePlanType) => {
            // Safari/iOS require navigator.clipboard.write to run inside the
            // user-gesture frame; see PlansList.handleCopyShareLink.
            const sharePromise = plansSharePostV2({
                jobId,
                pageId: pageId,
                bidId:
                    sourceEntityType === EntityType.BidPackage && sourceEntityId
                        ? sourceEntityId
                        : undefined,
            });
            try {
                const clipboardItem = new ClipboardItem({
                    "text/plain": sharePromise.then(
                        (resp) => new Blob([resp.data.shareableLink ?? ""], { type: "text/plain" }),
                    ),
                });
                await navigator.clipboard.write([clipboardItem]);
                const resp = await sharePromise;
                if (resp.data.shareableLink) {
                    void message.success("Share link copied");
                }
            } catch {
                const resp = await sharePromise;
                if (resp.data.shareableLink) {
                    await navigator.clipboard.writeText(resp.data.shareableLink);
                    void message.success("Share link copied");
                }
            }
        },
        [jobId, sourceEntityId, sourceEntityType],
    );

    const onGenerateQrCodePage = useCallback(
        async (_shareType: SharePlanType, pageId?: string, qrCodeTitle?: string) => {
            const resp = await plansSharePostV2({ jobId, pageId });
            if (resp.data.shareableLink) {
                if (qrCodeTitle) {
                    setPlanPageTitle(qrCodeTitle);
                }
                setShareData(resp.data);
                setShowGenerateQRCodeModal(true);
            }
        },
        [jobId],
    );

    const handlePlanClick = useCallback(
        (plan: PlanPage) => {
            if (plan.pageId) {
                if (isExternal && shareToken) {
                    const path = routes.plans.getExternalViewerLink(shareToken, plan.pageId);
                    const state: IPreviousLocationState = {
                        previous: {
                            pathname: getPreviousPathname(),
                            title: previousPageTitle ?? getPreviousTitle(),
                        },
                    };

                    navigate(path, { state });
                } else {
                    const path = `${routes.plans.getViewerLink(
                        plan.pageId,
                        jobId.toString(),
                        true,
                        sourceEntityType === EntityType.BidPackage && sourceEntityId
                            ? sourceEntityId.toString()
                            : undefined,
                    )}`;

                    if (isInWebview) {
                        navigate(path);
                    } else {
                        void window.open(
                            `${routes.appBase}${path}`,
                            "_blank",
                            "noopener,noreferrer",
                        );
                    }
                }
            }
        },
        [
            jobId,
            sourceEntityId,
            sourceEntityType,
            isExternal,
            shareToken,
            navigate,
            previousPageTitle,
            isInWebview,
        ],
    );

    const generateQrCodeModalConfig = {
        beforeClose: () => {
            setShowGenerateQRCodeModal(false);
        },
        parentRoute: location.pathname,
    };

    const planPages = useMemo(() => [...(planPageData?.data.planPage ?? [])], [planPageData]);

    const linkedCount = useMemo(() => {
        if (!linkedPlanPageIds || linkedPlanPageIds.size === 0) return 0;
        return planPages.reduce((n, page) => (linkedPlanPageIds.has(page.pageId) ? n + 1 : n), 0);
    }, [planPages, linkedPlanPageIds]);

    const showTabs = linkedCount > 0;

    const [activeTab, setActiveTab] = useState<PlansFilterTab>("fullSet");
    const hasInitializedDefaults = useRef(false);

    // First-mount default: jump to the Flagged tab when this set has any linked pages.
    // Runs once per instance — never overrides a manual tab change made later.
    useEffect(() => {
        if (hasInitializedDefaults.current) return;
        hasInitializedDefaults.current = true;
        if ((linkedPlanPageIds?.size ?? 0) > 0) {
            setActiveTab("flagged");
        }
    }, [linkedPlanPageIds]);

    useEffect(() => {
        const parentLinkedCount = linkedPlanPageIds?.size ?? 0;
        if (parentLinkedCount === 0 && activeTab === "flagged") {
            setActiveTab("fullSet");
        }
    }, [linkedPlanPageIds, activeTab]);

    const filteredPlanPages = useMemo(() => {
        if (!showTabs) return planPages;
        if (activeTab === "fullSet") return planPages;
        return planPages.filter((page) => linkedPlanPageIds?.has(page.pageId) ?? false);
    }, [planPages, showTabs, activeTab, linkedPlanPageIds]);

    return (
        <>
            <div className="LinkedPlanSetViewer">
                {showTabs && (
                    <BTTabs
                        className="plans-grid-tabs"
                        activeKey={activeTab}
                        onChange={(key) => setActiveTab(key as PlansFilterTab)}
                        data-testid="linked-plan-set-toolbar"
                    >
                        <BTTabs.TabPane
                            key="flagged"
                            tab={
                                <span
                                    data-testid="filter-linked-plan-pages"
                                    className="LinkedPlanSetTab"
                                >
                                    <BdsIconFlag /> Flagged ({linkedCount})
                                </span>
                            }
                        />
                        <BTTabs.TabPane
                            key="fullSet"
                            tab={
                                <span
                                    data-testid="filter-full-plan-set"
                                    className="LinkedPlanSetTab"
                                >
                                    <BdsIconPlans /> Full plan set ({planPages.length})
                                </span>
                            }
                        />
                    </BTTabs>
                )}
                {isLoading ? (
                    <div className="plans-grid">
                        {Array.from({ length: 4 }).map((_, index) => (
                            <PlansCardSkeleton
                                key={`skeleton-${index}`}
                                data-testid={`plansCardSkeleton-${index}`}
                            />
                        ))}
                    </div>
                ) : (
                    planPages && (
                        <div className="plans-grid">
                            {filteredPlanPages.map((plan: PlanPage) => (
                                <PlansCard
                                    onClick={handlePlanClick}
                                    key={plan.pageId}
                                    plan={plan}
                                    jobId={isExternal ? 0 : jobId}
                                    onCopySharePageLink={isExternal ? noOp : onCopySharePageLink}
                                    onGenerateQrCodePage={isExternal ? noOp : onGenerateQrCodePage}
                                    isInLinkedPlanSetViewer
                                    bidId={
                                        sourceEntityType === EntityType.BidPackage
                                            ? sourceEntityId
                                            : undefined
                                    }
                                    shareToken={isExternal ? ANONYMOUS_TOKEN : undefined}
                                    hasAnnotations={Boolean(plan.hasAnnotations)}
                                    canLinkToSourceEntity={canLinkPlanPages && !isExternal}
                                    isLinkedToSourceEntity={
                                        plan.pageId
                                            ? (linkedPlanPageIds?.has(plan.pageId) ?? false)
                                            : false
                                    }
                                    onToggleLinkToSourceEntity={onTogglePlanPageLink}
                                />
                            ))}
                        </div>
                    )
                )}
            </div>
            {!isExternal && showGenerateQRCodeModal && (
                <GenerateQRCodeModal
                    exportHeader={shareData?.exportHeaderResponse as ExportHeaderResponse}
                    shareUrl={shareData?.shareableLink}
                    title={planPageTitle ?? "Plans"}
                    modalConfig={generateQrCodeModalConfig}
                    bottomDescriptionMarkup={
                        <>
                            Use your phone's camera to open in Buildertrend
                            <br />
                            Anyone can view this file
                        </>
                    }
                />
            )}
        </>
    );
}
